import { parseCookies } from "nookies";
import axiosInternal from "./axiosInternal";
import { handleHeader } from "./cookie";
import { persistOtpLength } from "./otpLength";
import { OTP_CLIENT_TIMEOUT_MS } from "./otpRequestTimeout";
import { captureMetaAttribution, getOrCreateLeadId } from "./ads/conversion-tracking";
import { buildTikTokTrackingPayload } from "./ads/tiktok-attribution";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Cloudflare / edge / no-response failures (e.g. the intermittent CF "error code: 1000"
// = DNS-points-to-prohibited-IP flap) die at the edge BEFORE the request reaches our
// Next proxy + backend, so the checkout never created an invoice — safe to retry from
// the browser. Real application errors (4xx with a JSON {meta}/{data} body, e.g. 401 /
// 422) are NOT transient and are never retried.
// axiosInternal rejects with `error.response` (object: .status / .data) when the server
// responded, or `error.message` (string) when there was no response at all.
export const isTransientEdgeError = (err) => {
  if (!err) return false;
  if (typeof err === "string") return true; // no response: network / timeout / DNS
  const status = err.status;
  if (status === 502 || status === 503 || (status >= 520 && status <= 530)) return true;
  // CF edge errors return a plain-text / HTML body ("error code: 1000"); app errors are JSON.
  const data = err.data;
  if (typeof data === "string") {
    const body = data.toLowerCase();
    return body.indexOf("error code") !== -1
      || body.indexOf("cloudflare") !== -1
      || body.indexOf("<!doctype") !== -1
      || body.indexOf("<html") !== -1;
  }
  return false; // JSON-bodied response = real app error, do not retry
};

// Retry a request only on transient edge/network errors (bounded, with backoff). Resolves
// with the value unchanged; re-throws the ORIGINAL error (response object or message
// string) when not transient or once retries are exhausted, so existing catch blocks
// keep working. Used for the payment checkout call so a transient Cloudflare flap does
// not surface as a failed payment.
export const retryOnEdgeError = async (fn, { retries = 2, baseDelayMs = 400 } = {}) => {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === retries || !isTransientEdgeError(err)) throw err;
      // eslint-disable-next-line no-await-in-loop
      await sleep(baseDelayMs * (attempt + 1));
    }
  }
  throw lastErr;
};

const buildMetaLeadScope = (data = {}) => {
  const source = (typeof window !== "undefined" && window.location)
    ? window.location.pathname
    : "";
  return [
    data.id || data.user_id || data.email || data.phone_number || data.phone,
    data.page_source || data.source,
    source,
  ].filter(Boolean).join("_");
};

// Attach Meta lead_id + attribution to the register body so the backend can fire
// the server-side CAPI Lead. Caller-supplied non-empty lead_id wins.
// Also attaches TikTok ttclid/_ttp (additive; Meta freeze intact).
const withMetaLeadFields = (data = {}) => {
  if (typeof window === "undefined") {
    return data;
  }
  try {
    const preferredLeadId = data && data.lead_id ? data.lead_id : "";
    const leadId = getOrCreateLeadId(preferredLeadId, buildMetaLeadScope(data));
    const attribution = captureMetaAttribution();
    const metaFields = {};
    if (leadId) metaFields.lead_id = leadId;
    if (attribution._fbp) metaFields._fbp = attribution._fbp;
    if (attribution._fbc) metaFields._fbc = attribution._fbc;
    if (attribution.fbclid) metaFields.fbclid = attribution.fbclid;
    if (attribution.meta_country) metaFields.meta_country = attribution.meta_country;
    if (attribution.current_landing_url) {
      metaFields.meta_current_url = attribution.current_landing_url;
      metaFields.meta_event_source_url = attribution.current_landing_url;
    }
    if (attribution.first_landing_url) metaFields.meta_first_landing_url = attribution.first_landing_url;
    if (attribution.client_user_agent) metaFields.meta_user_agent = attribution.client_user_agent;
    const tiktokFields = buildTikTokTrackingPayload();
    return {
      ...metaFields,
      ...tiktokFields,
      ...data,
      lead_id: (data && data.lead_id) || metaFields.lead_id,
    };
  } catch (e) {
    return data;
  }
};


// ga dipake
export const getInternalHome = (params = {}, cookie = {}) => axiosInternal({
  url: "/api/home",
  method: "GET",
  params,
  headers: handleHeader(cookie),
});

// done cf
export const getInternalForgeRedeem = (params = {}, ctx = {}) => axiosInternal({
  url: "/api/workout/forge-redeem",
  method: "GET",
  params,
  ctx
});

// done cf
export const getInternalFreePremium = (params = {}, ctx = {}) => axiosInternal({
  url: "/api/workout/free-premium",
  method: "GET",
  params,
  ctx
});


// done cf
export const getInternalPackageList = (params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/workout/package-list",
  method: "GET",
  params,
  headers,
  ctx
});

// done cf
export const getInternalWorkoutLanding = (params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/workout-landing",
  method: "GET",
  params,
  headers,
  ctx
});

export const getQuestionnaireV2Landing = (params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/questionnaire-v2/workout-landing",
  method: "GET",
  params,
  headers,
  ctx
});

// done cf
export const getLandingPageContentInternal = (params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/workout-landing/landing-page-data",
  method: "GET",
  params,
  headers,
  ctx
});

export const connectInternalGarmin = (body = {}, headers = {}) => axiosInternal({
  url: "/api/connect-apps/garmin",
  method: "POST",
  data: body,
  headers,
});

export const connectInternalStrava = (body = {}, headers = {}) => axiosInternal({
  url: "/api/connect-apps/strava",
  method: "POST",
  data: body,
  headers,
});


// done cf
export const saveInternalQuestionnaire = (body = {}, headers = {}) => axiosInternal({
  url: "/api/workout-landing/save-questionnaire",
  method: "POST",
  data: body,
  headers,
});
export const saveQuestionnaireV2 = (body = {}, headers = {}) => axiosInternal({
  url: "/api/questionnaire-v2/plan",
  method: "POST",
  data: body,
  headers,
});
export const createQuestionnaireInternalFitTracker = (headers = {}) => axiosInternal({
  url: "/api/workout-landing/create-questionnaire-fit-tracker",
  method: "POST",
  headers,
});
export const createQuestionnaireV2FitTracker = (headers = {}) => axiosInternal({
  url: "/api/questionnaire-v2/fit-tracker-goal",
  method: "POST",
  headers,
});

export const checkLimitCollabInternal = (params = {}, headers = {}) => axiosInternal({
  url: "/api/workout-landing/check-limit-landing-page",
  method: "GET",
  headers,
  params,
});

// done cf
export const unsubNewsLetter = (body = {}, headers = {}) => axiosInternal({
  url: "/api/news-letter/unsubscribe",
  method: "POST",
  data: body,
  headers,
});


// done cf
export const activateNewsLetter = (body = {}, headers = {}, ctx) => axiosInternal({
  url: "/api/news-letter/activate",
  method: "POST",
  data: body,
  headers,
  ctx
});

// done cf
export const fetchAffiliateContent = (params = {}, ctx) => axiosInternal({
  url: "/api/affiliate/content",
  method: "GET",
  params,
  ctx
});

// done cf
export const fetchResellerContent = (params = {}, ctx = {}) => axiosInternal({
  url: "/api/affiliate/content-reseller",
  method: "GET",
  params,
  ctx
});


// done cf
export const loginAffiliateInternal = (body = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/affiliate/login",
  method: "POST",
  data: body,
  headers,
  ctx
});

// done cf
export const loginResellerInternal = (body = {}, headers = {}, ctx) => axiosInternal({
  url: "/api/affiliate/login-reseller",
  method: "POST",
  data: body,
  headers,
  ctx
});


// share workout - no need cf
export const getDetailWorkoutInternal = (params = {}) => axiosInternal({
  url: "/api/workout/detail",
  method: "GET",
  params,
});

// share meditation - no need cf
export const getDetailMeditationInternal = (params = {}) => axiosInternal({
  url: "/api/workout/meditation-detail",
  method: "GET",
  params,
});


// done cf
export const getInternalArticleList = (params = {}, cookie = {}, ctx = {}) => axiosInternal({
  url: "/api/article/list",
  method: "GET",
  params,
  headers: handleHeader(cookie),
  ctx
});

export const getInternalArticleByCategory = (params = {}, cookie = {}, ctx = {}) => axiosInternal({
  url: "/api/article/category",
  method: "GET",
  params,
  headers: handleHeader(cookie),
  ctx
});

export const getInternalArticleDetail = (id, params = {}, cookie = {}) => axiosInternal({
  url: `/api/article/detail/${id}`,
  method: "GET",
  params,
  headers: handleHeader(cookie),
});

export const likeInternalArticle = (body = {}) => axiosInternal({
  url: "/api/article/like",
  method: "POST",
  data: body,
});


// done cf
export const loginInternal = (body = {}) => axiosInternal({
  url: "/api/login/default",
  method: "POST",
  data: body,
});

// done cf
export const loginInternalGoogle = (body = {}) => axiosInternal({
  url: "/api/login/google",
  method: "POST",
  data: body,
});

// done cf
export const resetInternalPassword = (body = {}) => axiosInternal({
  url: "/api/user/reset-password",
  method: "POST",
  data: body,
});

export const getContentInternal = (params = {}) => axiosInternal({
  url: "/api/share-content",
  method: "GET",
  params,
});

export const getProgramDetailInternal = (params = {}) => axiosInternal({
  url: "/api/program/detail",
  method: "GET",
  params,
});

export const getInternalPoint = (params = {}) => axiosInternal({
  url: "/api/point",
  method: "GET",
  params,
});

// done cf
export const getInternalPackage = (params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/workout/package",
  method: "GET",
  params,
  headers,
  ctx
});

// done cf
export const getInternalProfile = (headers = {}, ctx = {}) => axiosInternal({
  url: "/api/user/profile",
  method: "GET",
  headers,
  ctx
});

// done cf
export const getInternalPayment = (body = {}, requestConfig = {}) => axiosInternal({
  ...requestConfig,
  url: "/api/user/payment-recurring",
  method: "POST",
  data: body,
});

// done cf
export const handleUnsubscribeInternal = (body = {}) => axiosInternal({
  url: "/api/user/unsubscribe",
  method: "POST",
  data: body,
});


// done cf
export const getInternalProduct = (id, params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: `/api/v1/product-packages/${id}`,
  method: "GET",
  params,
  headers,
  ctx
});


// Cross-source identity link with the affiliate click funnel: helpers/affiliateClick.js
// (server-only, deliberately never imported into a client bundle — see its file header)
// sets this same cookie on /a/:code and /r/:code — 32 hex chars, 1yr, path '/', not
// httpOnly. Reused here so a voucher applied at checkout can be deduped against an
// earlier anonymous link click by the same browser. The name is duplicated on purpose
// rather than imported from affiliateClick.js.
export const AFFILIATE_VISITOR_COOKIE = "mfs_vid";
const AFFILIATE_VISITOR_RE = /^[a-f0-9]{32}$/i;

// Reads mfs_vid via nookies (isomorphic: document.cookie on the client; req.headers.cookie
// on the server when a getInitialProps ctx is passed). Returns "" — never a fabricated
// id — when the cookie is absent or malformed, so callers can omit the field entirely.
export const getAffiliateVisitorId = (ctx = null) => {
  try {
    const cookies = ctx ? parseCookies(ctx) : parseCookies({});
    const value = (cookies && cookies[AFFILIATE_VISITOR_COOKIE]) || "";
    return AFFILIATE_VISITOR_RE.test(value) ? value.toLowerCase() : "";
  } catch (e) {
    return "";
  }
};

// Attaches visitor_id to a voucher-apply body when a valid mfs_vid cookie exists.
// Omitted entirely (not "") when absent/malformed — the backend reads a missing
// visitor_id as "identity unknown" and falls back to the user id.
const withAffiliateVisitorId = (body = {}, ctx = null) => {
  const visitorId = getAffiliateVisitorId(ctx);
  return visitorId ? { ...body, visitor_id: visitorId } : body;
};

// done cf
export const submitInternalVoucher = (body = {}, headers = {}, ctx = null) => axiosInternal({
  url: "/api/purchases",
  method: "POST",
  data: withAffiliateVisitorId(body, ctx),
  headers,
});

// done cf
// done cf
export const internalMidtransCheckout = (body = {}, params = {}, headers = {}) => retryOnEdgeError(() => axiosInternal({
  url: "/api/purchases/checkout",
  method: "POST",
  data: body,
  params,
  headers
}));

// Trello 546: /xt lane proxy — same contract as the checkout call above, but
// the API route forwards to the backend's xt-checkout endpoint which engages
// the payment orchestrator. Purchases page selects this only when ?xt=1.
export const internalXtCheckout = (body = {}, params = {}, headers = {}) => retryOnEdgeError(() => axiosInternal({
  url: "/api/purchases/xt-checkout",
  method: "POST",
  data: body,
  params,
  headers
}));


// done cf
export const internalInvoices = (
  id,
  body = {},
  headers = {},
  ctx = {},
  requestConfig = {}
) => axiosInternal({
  ...requestConfig,
  url: `/api/purchases/invoices/${id}`,
  method: "GET",
  params: body,
  headers,
  ctx
});

export const getStaticDetail = (id, params = {}) => axiosInternal({
  url: `/api/static/${id}`,
  method: "GET",
  params,
});

export const getInternalContactCategory = (params = {}) => axiosInternal({
  url: "/api/static/contact-category",
  method: "GET",
  params,
});

export const postInternalContact = (data = {}) => axiosInternal({
  url: "/api/static/contact-us",
  method: "POST",
  data,
});

export const getInternalFoodList = (params = {}) => axiosInternal({
  url: "/api/food/list",
  method: "GET",
  params,
});

export const getInternalFoodDetail = (params = {}) => axiosInternal({
  url: "/api/food/detail",
  method: "GET",
  params,
});

export const getInternalChallengeDetail = (id) => axiosInternal({
  url: `/api/challenge/${id}`,
  method: "GET",
});

export const getInternalCookingList = (params = {}) => axiosInternal({
  url: "/api/cooking/list",
  method: "GET",
  params,
});

export const handleLikeInternalCooking = (data = {}) => axiosInternal({
  url: "/api/cooking/like",
  method: "POST",
  data,
});

export const getInternalCookingDetail = (id, params = {}, headers) => axiosInternal({
  url: `/api/cooking/detail/${id}`,
  method: "GET",
  params,
  headers,
});

// done cf
export const verifyInternalChangeEmail = (id, ctx = {}) => axiosInternal({
  url: `/api/user/verify-change-email/${id}`,
  method: "GET",
  ctx
});

export const getInternalAppsChallengeDetail = (id, headers = {}) => axiosInternal({
  url: `/api/challenge/detail/${id}`,
  method: "GET",
  headers,
});

export const handleParticipateInternalChallenge = (id, headers = {}) => axiosInternal({
  url: `/api/challenge/participate/${id}`,
  method: "GET",
  headers,
});

// done cf
export const getInternalActivePackage = (headers = {}, ctx = {}) => axiosInternal({
  url: "/api/purchases/active-package",
  method: "GET",
  headers,
  ctx
});

// done cf
export const getInternalListVoucher = (params = {}) => axiosInternal({
  url: "/api/user/list-voucher",
  method: "GET",
  params,
});

// done cf
// Trello 437: resend-OTP responses may carry the server-driven `otp_length`
// flag — persist it so OTP pages render the backend's cell count.
// Trello 581 X1: bounded — axiosInternal has no timeout of its own, so a hung
// route would otherwise leave the loader up indefinitely.
export const activateInternalEmail = (data = {}) => axiosInternal({
  url: "/api/user/activate-email",
  method: "POST",
  data,
  timeout: OTP_CLIENT_TIMEOUT_MS,
}).then((resp) => { persistOtpLength(resp); return resp; });

// done cf
// Trello 437: the activation response may carry `otp_length` too — persist it.
// Trello 581 X1: bounded, same reason as activateInternalEmail.
export const activateEmailByOTP = (data = {}) => axiosInternal({
  url: "/api/user/activate-account-otp",
  method: "POST",
  data,
  timeout: OTP_CLIENT_TIMEOUT_MS,
}).then((resp) => { persistOtpLength(resp); return resp; });

// done cf
// Trello 437: the register response may carry the server-driven `otp_length`
// flag — persist it so later OTP pages render the backend's cell count.
export const registerInternal = (data = {}, params = {}) => axiosInternal({
  url: "/api/user/register",
  method: "POST",
  data: withMetaLeadFields(data),
  params,
}).then((resp) => { persistOtpLength(resp); return resp; });

// done cf
export const forgotPasswordInternal = (data = {}) => axiosInternal({
  url: "/api/user/forgot-password",
  method: "POST",
  data,
});

// done cf
export const completeInternalLogin = (data = {}) => axiosInternal({
  url: "/api/user/complete-registration",
  method: "POST",
  data,
});


// done cf
export const internalActivateByToken = (data = {}, ctx = {}) => axiosInternal({
  url: "/api/user/activate-user-by-token",
  method: "POST",
  data,
  ctx
});

// done cf
export const redeemInternalVoucher = (data = {}) => axiosInternal({
  url: "/api/user/redeem-voucher",
  method: "POST",
  data,
});

// done cf
export const setInternalLanguage = (data = {}, headers = {}) => axiosInternal({
  url: "/api/set-language",
  method: "POST",
  data,
  headers,
});

// done cf
export const checkCollaborationVoucher = (params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/collab/voucher",
  method: "GET",
  params,
  headers,
  ctx
});

// done cf
export const checkCollaborationContent = (params = {}, headers = {}, ctx = {}) => axiosInternal({
  url: "/api/collab/content",
  method: "GET",
  params,
  headers,
  ctx
});

// done cf
export const setInternalEmailPreference = (data = {}, headers = {}) => axiosInternal({
  url: "/api/user/email-preference",
  method: "POST",
  data,
  headers
});


export const logEventData = (data = {}, headers = {}) => axiosInternal({
  url: "/api/log-event",
  method: "POST",
  data,
  headers
});
